import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { find, map } from 'lodash';
import React from 'react';

import { BulkSlideTagOperation, BulkSlideTagResultScopeMode } from 'api/slideTags';
import { SlideTag } from 'interfaces/slideTag';
import { operationDescriptions, operationLabels, resultScopeLabels } from './constants';

interface BulkSlideTagConfigureStepProps {
  hasExplicitSlideSelection: boolean;
  operation: BulkSlideTagOperation;
  setOperation: React.Dispatch<React.SetStateAction<BulkSlideTagOperation>>;
  selectedTags: SlideTag[];
  setSelectedTags: React.Dispatch<React.SetStateAction<SlideTag[]>>;
  slideTagOptions: SlideTag[];
  isLoadingSlideTagOptions: boolean;
  needsExperimentResultScope: boolean;
  resultScopeMode: BulkSlideTagResultScopeMode;
  setResultScopeMode: React.Dispatch<React.SetStateAction<BulkSlideTagResultScopeMode>>;
  activeFilterOrchestrationId?: string;
  explicitOrchestrationId?: string;
  setExplicitOrchestrationId: React.Dispatch<React.SetStateAction<string | undefined>>;
  orchestrationOptions: { orchestrationId: string }[];
  isLoadingOrchestrations: boolean;
  operationRequiresTags: (operation: BulkSlideTagOperation) => boolean;
  disableRemove: boolean;
  disableClear: boolean;
}

export const BulkSlideTagConfigureStep: React.FC<BulkSlideTagConfigureStepProps> = ({
  hasExplicitSlideSelection,
  operation,
  setOperation,
  selectedTags,
  setSelectedTags,
  slideTagOptions,
  isLoadingSlideTagOptions,
  needsExperimentResultScope,
  resultScopeMode,
  setResultScopeMode,
  activeFilterOrchestrationId,
  explicitOrchestrationId,
  setExplicitOrchestrationId,
  orchestrationOptions,
  isLoadingOrchestrations,
  operationRequiresTags,
  disableRemove,
  disableClear,
}) => (
  <Stack spacing={2}>
    {!hasExplicitSlideSelection && <Alert severity="warning">Select slides before running bulk slide tagging.</Alert>}

    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Operation
      </Typography>
      <ToggleButtonGroup
        color="primary"
        exclusive
        value={operation}
        onChange={(_, value) => {
          if (!value) return;
          const nextOperation = value as BulkSlideTagOperation;
          setOperation(nextOperation);
          if (!operationRequiresTags(nextOperation)) {
            setSelectedTags([]);
          }
        }}
        size="small"
      >
        {map(operationLabels, (label, key) => (
          <ToggleButton
            value={key}
            key={key}
            disabled={(key === 'remove' && disableRemove) || (key === 'clear' && disableClear)}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {operationDescriptions[operation]}
      </Typography>
      {(disableRemove || disableClear) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Nothing to clear. Selected slides have no tags.
        </Typography>
      )}
    </Box>

    {operationRequiresTags(operation) && (
      <Autocomplete
        multiple
        options={slideTagOptions}
        loading={isLoadingSlideTagOptions}
        value={selectedTags}
        getOptionLabel={(option) => option.tagValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => setSelectedTags(value)}
        renderTags={(value, getTagProps) =>
          map(value, (option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={`${option.tagValue}${
                option.perExperimentResult ? ' (result)' : option.perStudy ? ' (study)' : ' (global)'
              }`}
            />
          ))
        }
        renderInput={(params) => <TextField {...params} label="Slide tags" />}
      />
    )}

    {needsExperimentResultScope && (
      <>
        <FormControl>
          <FormLabel>Experiment-result scope</FormLabel>
          <RadioGroup
            value={resultScopeMode}
            onChange={(event) => setResultScopeMode(event.target.value as BulkSlideTagResultScopeMode)}
          >
            <FormControlLabel
              value="filter"
              control={<Radio />}
              disabled={!activeFilterOrchestrationId}
              label={`${resultScopeLabels.filter}${
                activeFilterOrchestrationId ? ` (${activeFilterOrchestrationId})` : ''
              }`}
            />
            <FormControlLabel value="explicit" control={<Radio />} label={resultScopeLabels.explicit} />
            <FormControlLabel
              value="approved_per_slide"
              control={<Radio />}
              label={resultScopeLabels.approved_per_slide}
            />
          </RadioGroup>
        </FormControl>

        {resultScopeMode === 'explicit' && (
          <Autocomplete
            options={orchestrationOptions}
            loading={isLoadingOrchestrations}
            value={find(orchestrationOptions, { orchestrationId: explicitOrchestrationId }) || null}
            getOptionLabel={(option) => option.orchestrationId}
            isOptionEqualToValue={(option, value) => option.orchestrationId === value.orchestrationId}
            onChange={(_, value) => setExplicitOrchestrationId(value?.orchestrationId)}
            renderInput={(params) => <TextField {...params} label="Orchestration" />}
          />
        )}
      </>
    )}
  </Stack>
);
